import React, { useState, useEffect, SyntheticEvent } from 'react';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import { styled } from '@mui/material/styles';
import SearchBar from 'ui/SearchBar/SearchBar';
import Loading from 'components/Loading';
import { useSafeState } from 'helpers/hooks';
import { Button } from '@mui/material';
import { SentenceTreeItem } from 'routes/Explore/components/Sentence/components/TreeDropdown';
import {
    filterAndExpandFieldSearchResults,
    TreeData,
} from 'routes/Explore/components/Sentence/helpers/treeData';
import Flex from 'ui/Flex/Flex';
import Domains from './Domains';
import { useGetDefaultTableToExpand } from '../hooks/treeData';

const StyTreeDialog = styled(Dialog, {
    name: 'StyTreeDialog',
})(({ theme }) => ({
    '& .MuiPaper-root': {
        height: '100%',
        [theme.breakpoints.up('sm')]: {
            minWidth: '600px',
        },
    },
}));

const StySearchBarWrapper = styled('div', {
    name: 'StySearchBarWrapper',
})({
    position: 'sticky',
    zIndex: 1,
    top: 0,
    left: 0,
    width: '100%',
    background: 'white',
    padding: '1.5rem',
});

const StyDialogContent = styled(DialogContent, {
    name: 'StyDialogContent',
})({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    overflow: 'hidden',
});

type Props = {
    treeData: TreeData;
    value?: string;
    testid: string;
    onClick?: (treeData: unknown) => void;
    onClose: () => void;
    open: boolean;
    isLoading: boolean;
    showConfirmButton?: boolean;
    isConfirmButtonDisabled?: boolean;
    onSubmit?: (
        item: SentenceTreeItem | SyntheticEvent<HTMLButtonElement, MouseEvent>,
    ) => void;
};

const Tree = ({
    treeData,
    value,
    testid,
    onClick = () => {},
    onClose,
    open,
    isLoading,
    showConfirmButton = false,
    isConfirmButtonDisabled = true,
    onSubmit = () => {},
}: Props): React.ReactElement => {
    const { t } = useTranslation();
    const defaultExpandedTreeData = useGetDefaultTableToExpand(treeData);
    const [expanded, setExpanded] = useSafeState<string[]>([], (a, b) =>
        _.isEqual(a, b),
    );

    const [query, setQuery] = useState('');
    const [filteredTreeData, setFilteredTreeData] = useState(treeData);

    const setDefaultExpandedTreeData = () => {
        setFilteredTreeData(treeData);
        setExpanded(defaultExpandedTreeData);
    };

    const filterAndExpandSearchResults = filterAndExpandFieldSearchResults(
        treeData,
        setFilteredTreeData,
        setExpanded,
    );

    const updateTree = (newQuery: string) => {
        if (newQuery !== query) setQuery(newQuery);

        if (newQuery) {
            filterAndExpandSearchResults(newQuery);
        } else {
            setDefaultExpandedTreeData();
        }
    };

    useEffect(() => {
        // Update is necessary because tree data is changed when the user modifies the request
        updateTree(query);
    }, [JSON.stringify(treeData)]);

    const onExited = () => {
        // When the popover is re-opened, the component will have been reset
        setQuery('');
        setDefaultExpandedTreeData();
    };

    const content = isLoading ? (
        <Loading />
    ) : (
        <Domains
            treeData={filteredTreeData}
            onClick={onClick}
            testid={testid}
            expanded={expanded}
            query={query}
            value={value}
            // @ts-expect-error [TS migration] Was not detected by flow
            onNodeToggle={(event, nodeIds) => {
                setExpanded(nodeIds);
            }}
        />
    );

    return (
        <StyTreeDialog
            open={open}
            onClose={onClose}
            TransitionProps={{ onExited }}
            data-testid={`${testid}-tree-dropdown-modal`}
        >
            <StyDialogContent>
                <Flex
                    direction="column"
                    align="stretch"
                    sx={{
                        width: '100%',
                        height: '100%',
                        overflow: 'auto',
                    }}
                >
                    <StySearchBarWrapper>
                        <SearchBar
                            handleChange={updateTree}
                            placeholder={t('explore.fieldSearchPlaceholder')}
                            autoFocus
                            testid={`${testid}-tree-dropdown-search`}
                        />
                    </StySearchBarWrapper>

                    {content}
                </Flex>
                {showConfirmButton && (
                    <Button
                        variant="contained"
                        size="small"
                        disabled={isConfirmButtonDisabled}
                        onClick={onSubmit}
                        sx={{ my: '1rem' }}
                    >
                        {t('common.confirm')}
                    </Button>
                )}
            </StyDialogContent>
        </StyTreeDialog>
    );
};

export default Tree;
